import { storeToRefs } from 'pinia';
import { createRouter, createWebHistory, createMemoryHistory } from 'vue-router';

import authenticate from '@js/credentials/authenticate';

import { endpoint } from '@js/dom/parent';
import googleAnalytics from '@js/dom/analytics';

import useAuthStore from '@/stores/auth';

import blank from './blank';

let _app;

export default {
  get app() {
    if (_app) {
      return _app;
    }

    _app = createRouter({
      history: endpoint() ? createMemoryHistory() : createWebHistory(),
      routes: [{
        path: '/',
        name: 'home-index',
        meta: { public: true, title: '404', subtitle: 'Por favor, escolha um endpoint existente' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/blank',
        name: 'home-blank',
        meta: { public: true, title: '204', subtitle: 'Nenhum conteúdo a ser exibido' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/unauthorized',
        name: 'home-unauthorized',
        meta: { public: true, title: '401', subtitle: 'O token (authorization) informado é inválido' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/:matches(.*)*',
        name: 'home-404',
        meta: { public: true, title: '404', subtitle: 'Por favor, escolha um endpoint existente' },
        component: () => import('@/views/Home/Message.vue'),
      }, {
        path: '/preview',
        name: 'preview-index',
        meta: { public: true },
        component: () => import('@/views/Preview/Index.vue'),
        children: [{
          path: 'faq',
          name: 'preview-faq',
          component: () => import('@/views/Preview/FAQ.vue'),
        }, {
          path: ':embed',
          name: 'preview-embed',
          component: () => import('@/views/Preview/Blank.vue'),
        }],
      }, {
        path: '/customers/form',
        name: 'customers-form',
        component: () => import('@/views/Customers/Form.vue'),
      }, {
        path: '/customers/list',
        name: 'customers-list',
        component: () => import('@/views/Customers/List.vue'),
      }, {
        path: '/customers/schema',
        name: 'customers-schema',
        meta: { public: true },
        component: () => import('@/views/Customers/Schema.vue'),
      }, {
        path: '/proposals/form',
        name: 'proposals-form',
        component: () => import('@/views/Proposals/Form.vue'),
      }, {
        path: '/proposals/success',
        name: 'proposals-success',
        component: () => import('@/views/Proposals/Success.vue'),
      }, {
        path: '/proposals/list',
        name: 'proposals-list',
        component: () => import('@/views/Proposals/List.vue'),
      }, {
        // O caminho do host é `/proposals/approved`, e o endpoint do embute repete-o: as
        // aprovadas são uma tela própria, com um filtro menor e um card de totais, e não
        // um filtro da lista geral.
        path: '/proposals/approved',
        name: 'proposals-approved',
        component: () => import('@/views/Proposals/Approved.vue'),
      }, {
        path: '/simulations/read',
        name: 'simulations-read',
        component: () => import('@/views/Simulations/Read.vue'),
      }, {
        path: '/simulations/form',
        name: 'simulations-form',
        component: () => import('@/views/Simulations/Form.vue'),
      }, {
        // A coleção é `/proposal_simulations` e não `/simulations`: o caminho aqui é o do
        // host (contrato), o endpoint da API é outro assunto.
        path: '/simulations/list',
        name: 'simulations-list',
        component: () => import('@/views/Simulations/List.vue'),
      }, {
        path: '/vehicles/form',
        name: 'vehicles-form',
        component: () => import('@/views/Vehicles/Form.vue'),
      }, {
        path: '/vehicles/import',
        name: 'vehicles-import',
        component: () => import('@/views/Vehicles/Import.vue'),
      }, {
        /*
         * A ARTE PARA STORY NÃO TEM ROTA, e é por isso que ela não aparece aqui: é um modal
         * da listagem de estoque (`views/Vehicles/List.vue` monta o
         * `views/Vehicles/Banner.vue`), que recebe o veículo por prop. Endpoint próprio
         * exigiria o id em `CREDERE.settings`, e era o que a página host do `web` fazia
         * antes de a arte voltar a ser modal.
         */
        path: '/vehicles/showroom',
        name: 'vehicles-showroom',
        component: () => import('@/views/Vehicles/Showroom.vue'),
      }, {
        path: '/vehicles/find',
        name: 'vehicles-find',
        component: () => import('@/views/Vehicles/Find.vue'),
      }, {
        path: '/vehicles/list',
        name: 'vehicles-list',
        component: () => import('@/views/Vehicles/List.vue'),
      }, {
        path: '/vehicle_models/form',
        name: 'vehicle-models-form',
        component: () => import('@/views/VehicleModels/Form.vue'),
      }, {
        path: '/vehicle_models/list',
        name: 'vehicle-models-list',
        component: () => import('@/views/VehicleModels/List.vue'),
      }, {
        /*
         * OS CONVITES ENVIADOS (embed#22), que eram a última listagem nativa da pwa. Só a
         * lista veio: o formulário de convite segue lá, e é a pwa que compõe as duas abas
         * de `/users/invitations` - esta rota é o conteúdo de uma delas.
         *
         * O caminho é o do host (contrato) e o `name` é nosso.
         */
        path: '/user_invitations/list',
        name: 'user-invitations-list',
        component: () => import('@/views/Invitations/List.vue'),
      }, {
        path: '/inbox/conversations',
        name: 'inbox-conversations',
        component: () => import('@/views/Inbox/List.vue'),
      }, {
        // Every public simulator screen lives under /public_simulator/ (wip#1342).
        // The path is the contract the host builds `CREDERE.src` from; the `name`
        // is ours alone (nothing outside this file references it). The flat
        // /public_simulator_settings/* and /public_simulator_distribution/* paths
        // they replace are gone, with no alias — web and pwa move with them.
        path: '/public_simulator/settings/form',
        name: 'public-simulator-settings-form',
        component: () => import('@/views/PublicSimulator/Settings/Form.vue'),
      }, {
        path: '/public_simulator/connections/form',
        name: 'public-simulator-connections-form',
        component: () => import('@/views/PublicSimulator/Connections/Form.vue'),
      }, {
        path: '/public_simulator/automations/form',
        name: 'public-simulator-automations-form',
        component: () => import('@/views/PublicSimulator/Automations/Form.vue'),
      }, {
        // The list is this app's too now (embed#22): it used to be a native pwa
        // screen that read the collection straight from the API and navigated to
        // the form above through its own router. It goes the other way round from
        // here - the row emits `public_simulator_automation:read` and the host
        // opens the form.
        path: '/public_simulator/automations/list',
        name: 'public-simulator-automations-list',
        component: () => import('@/views/PublicSimulator/Automations/List.vue'),
      }, {
        // Same shape as the automations' form, and for the same reason.
        path: '/public_simulator/notifications/form',
        name: 'public-simulator-notifications-form',
        component: () => import('@/views/PublicSimulator/Notifications/Form.vue'),
      }, {
        path: '/public_simulator/notifications/list',
        name: 'public-simulator-notifications-list',
        component: () => import('@/views/PublicSimulator/Notifications/List.vue'),
      }, {
        // Where the store answers, once, what the assistant answers for it from
        // then on (wip#1381). It edits the SAME entity as the settings screen
        // above (public_simulator_settings), so saving it emits
        // public_simulator_setting:update and not a type of its own; the path is
        // the contract the host builds `CREDERE.src` from, and it exists in web
        // and pwa too.
        path: '/public_simulator/answers/form',
        name: 'public-simulator-answers-form',
        component: () => import('@/views/PublicSimulator/Answers/Form.vue'),
      }, {
        // Where the Eita writes ONE question of that catalogue (wip#1381) -
        // `new` and `edit` are both this route, and what tells them apart is the
        // id the host injects.
        path: '/public_simulator/questions/form',
        name: 'public-simulator-questions-form',
        component: () => import('@/views/PublicSimulator/Questions/Form.vue'),
      }, {
        path: '/public_simulator/questions/list',
        name: 'public-simulator-questions-list',
        component: () => import('@/views/PublicSimulator/Questions/List.vue'),
      }, {
        path: '/public_simulator/find',
        name: 'public-simulator-find',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Find/Form.vue'),
      }, {
        path: '/public_simulator/contact',
        name: 'public-simulator-contact',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Contact/Form.vue'),
      }, {
        path: '/public_simulator/success',
        name: 'public-simulator-success',
        meta: { public: true },
        component: () => import('@/views/PublicSimulator/Success.vue'),
      }, {
        path: '/onboarding/register',
        name: 'onboarding-register',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Register.vue'),
      }, {
        path: '/onboarding/profile',
        name: 'onboarding-profile',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Profile.vue'),
      }, {
        path: '/onboarding/store',
        name: 'onboarding-store',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Store.vue'),
      }, {
        path: '/onboarding/address',
        name: 'onboarding-address',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Address.vue'),
      }, {
        path: '/onboarding/subscription',
        name: 'onboarding-subscription',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Subscription.vue'),
      }, {
        path: '/onboarding/credentials',
        name: 'onboarding-credentials',
        meta: { public: true },
        component: () => import('@/views/Onboarding/Credentials.vue'),
      }, {
        path: '/reports/score',
        name: 'reports-score',
        component: () => import('@/views/Reports/Score.vue'),
      }, {
        path: '/system_alerts/form',
        name: 'system-alerts-form',
        component: () => import('@/views/SystemAlerts/Form.vue'),
      }, {
        path: '/system_alerts/list',
        name: 'system-alerts-list',
        component: () => import('@/views/SystemAlerts/List.vue'),
      }, {
        path: '/bank_commission_rules/form',
        name: 'bank-commission-rules-form',
        component: () => import('@/views/BankCommissionRules/Form.vue'),
      }, {
        path: '/bank_ranking/form',
        name: 'bank-ranking-form',
        component: () => import('@/views/BankRanking/Form.vue'),
      }, {
        /*
         * THE SAME SCREEN, TWICE (embed#22). Two host routes open a list of stores to
         * pick one from, and what used to differ between them - where the click went -
         * stopped being this app's business: the row emits `store:read` and the host,
         * which knows which of its own pages it is, routes. What is left is the
         * `purpose`, and it is the route that says it, because the route IS the
         * difference. It also names the group the host writes its settings under -
         * `CREDERE.settings.<purpose>.stores`.
         *
         * A third purpose is a route here, a line in `views/Stores/javascripts/purposes.js`
         * and nothing else.
         *
         * ERAM TRÊS: o ranking de bancos e as comissões tinham cada um a sua, e as duas
         * levavam a telas da mesma área. Viraram esta, porque o controle comercial passou a
         * ter uma porta só, com um menu por loja atrás dela.
         */
        path: '/commercial_control/stores',
        name: 'commercial-control-stores',
        props: { purpose: 'commercial_control' },
        component: () => import('@/views/Stores/List.vue'),
      }, {
        path: '/public_simulator/stores',
        name: 'public-simulator-stores',
        props: { purpose: 'public_simulator' },
        component: () => import('@/views/Stores/List.vue'),
      }, {
        /*
         * OS DOIS TIPOS DE COMISSÃO DE UMA LOJA, e é um MENU e não uma listagem: duas
         * linhas fixas, uma tela cada, sem coleção nem request. Por isso é o
         * `Settings/Menu` com mais uma tabela (`views/Settings/javascripts/menus.js`).
         *
         * A loja não está no caminho: quem tem o id é o host, e é dele que saem as URLs
         * dos dois tipos.
         */
        path: '/bank_commission_rules/menu',
        name: 'bank-commission-rules-menu',
        props: { menu: 'bank_commission_rules' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        /*
         * O MENU DO SHOWROOM DE UMA LOJA, e o mesmo arranjo do menu acima: quatro linhas
         * fixas, uma tela cada, sem coleção nem request - logo o `Settings/Menu` com mais
         * uma tabela (`views/Settings/javascripts/menus.js`).
         *
         * A loja não está no caminho: quem tem o id é o host, e é dele que saem as URLs
         * das quatro telas.
         *
         * E CADA UMA DESSAS URLS É UMA PÁGINA DO `web`, hospedada pela pwa, que monta um dos
         * `public_simulator/<tela>/form` daqui. Ou seja: o destino de uma linha deste menu é
         * uma tela deste mesmo app, alcançada dando a volta pelo host - que é quem conhece o
         * caminho e quem tem o id da loja.
         */
        path: '/public_simulator/menu',
        name: 'public-simulator-menu',
        props: { menu: 'public_simulator' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/public_simulator/distribution/form',
        name: 'public-simulator-distribution-form',
        component: () => import('@/views/PublicSimulator/Distribution/Form.vue'),
      }, {
        /*
         * OS TRÊS MENUS DE `/settings`, e a mesma tela nas três (embed#22). Eles não têm
         * coleção nenhuma - são listas de linhas fixas que abrem uma tela cada -, então o
         * que difere entre as rotas é a tabela de itens (`views/Settings/javascripts/menus`)
         * e o grupo sob o qual o host escreve as URLs: `CREDERE.settings.<menu>.menu`.
         *
         * `/settings/about` é o menu com os dois links do site (termos e privacidade), e
         * está aqui pelo mesmo motivo dos outros dois: é uma tela de menu da pwa.
         */
        path: '/settings/menu',
        name: 'settings-menu',
        props: { menu: 'settings' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/settings/commercial/menu',
        name: 'settings-commercial-menu',
        props: { menu: 'commercial' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        path: '/settings/about/menu',
        name: 'settings-about-menu',
        props: { menu: 'about' },
        component: () => import('@/views/Settings/Menu.vue'),
      }, {
        /*
         * AS TRÊS TELAS COMERCIAIS, UMA SÓ (embed#22). Elas já eram o mesmo componente de
         * lista na pwa, com três coleções diferentes; o que muda é a coleção e as
         * informações que a linha mostra, e isso é uma tabela
         * (`views/Commercial/javascripts/purposes.js`) e não três telas.
         *
         * O FORMULÁRIO DAS TRÊS CONTINUA NA PWA: a linha emite a mensagem da coleção e o
         * host abre o formulário lá. Só a lista veio.
         */
        path: '/financing_return_rules/list',
        name: 'financing-return-rules-list',
        props: { purpose: 'financing_return_rules' },
        component: () => import('@/views/Commercial/List.vue'),
      }, {
        path: '/vehicle_prices/list',
        name: 'vehicle-prices-list',
        props: { purpose: 'vehicle_prices' },
        component: () => import('@/views/Commercial/List.vue'),
      }, {
        path: '/financing_product_rules/list',
        name: 'financing-product-rules-list',
        props: { purpose: 'financing_product_rules' },
        component: () => import('@/views/Commercial/List.vue'),
      }],
    });

    _app.beforeEach(blank);

    _app.beforeEach(async (to) => {
      if (to.meta.public) {
        return true;
      }

      const authStore = useAuthStore();
      const { user } = storeToRefs(authStore);

      if (user.value.id) {
        return true;
      }

      const failure = await authenticate();

      if (failure) {
        return {
          name: 'home-unauthorized',
          query: {
            title: failure.code,
            subtitle: failure.message,
          },
        };
      }

      return true;
    });

    _app.afterEach((to) => {
      const authStore = useAuthStore();
      const { user } = storeToRefs(authStore);

      if (user.value.id) {
        googleAnalytics(to, user.value.id);
      }
    });

    // const IMPORT_MODULE_ERRORS = [
    //   'Failed to fetch dynamically imported module',
    //   'Importing a module script failed',
    // ];

    // router.onError((error) => {
    //   if (!IMPORT_MODULE_ERRORS.some((i) => error.message.includes(i))) {
    //     /* eslint-disable no-console */
    //     console.log('ROUTER/ONERROR FAILURE:');
    //     console.error(error);
    //     /* eslint-enable no-console */
    //   }
    // });

    return _app;
  },
};
